/**
 * Shape of a deleted-or-blanked media asset. Photo and VideoAsset both carry `url` + `deleted`,
 * so the same two signals decide tombstoning for either.
 */
type TombstonableAsset = { url?: string; deleted?: boolean } | null | undefined;

export const isPhotoTombstoned = (photo: TombstonableAsset): boolean => {
  if (!photo) return false;
  if (photo.deleted === true) return true;
  if (photo.url === '') return true;
  return false;
};

type MediaPhotoMember = { __typename?: 'MediaPhoto' | 'MediaVideo'; photo?: TombstonableAsset };
type MediaVideoMember = { __typename?: 'MediaPhoto' | 'MediaVideo'; videoAsset?: TombstonableAsset };

/**
 * Deliberately photo-only: it is the filter that keeps a video off a surface that can only show
 * photos — cover-image pickers, colour palettes, header photos. Most `EventMedia` consumers want
 * exactly this, so it stays narrow. Use `isActiveMedia` on a surface that renders both kinds.
 */
export const isActiveMediaPhoto = <T extends MediaPhotoMember>(m: T): m is Extract<T, { __typename?: 'MediaPhoto' }> =>
  m.__typename === 'MediaPhoto' && !isPhotoTombstoned(m.photo);

/**
 * A video with no `videoAsset` is inactive: the asset is the only thing there is to play, and the
 * field is nullable because it resolves across services.
 */
export const isActiveMediaVideo = <T extends MediaVideoMember>(m: T): m is Extract<T, { __typename?: 'MediaVideo' }> =>
  m.__typename === 'MediaVideo' && !!m.videoAsset && !isPhotoTombstoned(m.videoAsset);

/** Keeps both arms of the `EventMedia` union — for a surface that renders photos and videos together. */
export const isActiveMedia = <T extends MediaPhotoMember | MediaVideoMember>(m: T): m is Extract<T, { __typename?: 'MediaPhoto' } | { __typename?: 'MediaVideo' }> =>
  isActiveMediaPhoto(m) || isActiveMediaVideo(m);
